// Enhanced text similarity calculator with contraction handling
export function calculateEnhancedSimilarity(text1: string, text2: string): number {
  if (!text1 || !text2) return 0;

  // Contraction mappings
  const contractionMap: Record<string, string> = {
    // Common contractions
    "we're": "we are",
    "they're": "they are",
    "you're": "you are",
    "i'm": "i am",
    "he's": "he is",
    "she's": "she is",
    "it's": "it is",
    "we've": "we have",
    "they've": "they have",
    "you've": "you have",
    "i've": "i have",
    "we'll": "we will",
    "they'll": "they will",
    "you'll": "you will",
    "i'll": "i will",
    "he'll": "he will",
    "she'll": "she will",
    "it'll": "it will",
    "won't": "will not",
    "can't": "cannot",
    "don't": "do not",
    "doesn't": "does not",
    "didn't": "did not",
    "isn't": "is not",
    "aren't": "are not",
    "wasn't": "was not",
    "weren't": "were not",
    "haven't": "have not",
    "hasn't": "has not",
    "hadn't": "had not",
    "wouldn't": "would not",
    "couldn't": "could not",
    "shouldn't": "should not",
    "mustn't": "must not",
    "needn't": "need not",
    "daren't": "dare not",
    "mayn't": "may not",
    "oughtn't": "ought not",
    "let's": "let us",
    "that's": "that is",
    "there's": "there is",
    "here's": "here is",
    "where's": "where is",
    "what's": "what is",
    "who's": "who is",
    "how's": "how is",
    "when's": "when is",
    "why's": "why is",
  };

  // Reverse mapping for expanded to contracted
  const expansionMap: Record<string, string> = {};
  Object.entries(contractionMap).forEach(([contracted, expanded]) => {
    expansionMap[expanded] = contracted;
  });

  // Enhanced normalize function
  const normalize = (text: string): string => {
    let normalized = text
      .toLowerCase()
      .replace(/[^a-z0-9\s']/g, '') // Keep apostrophes for contractions
      .replace(/\s+/g, ' ')
      .trim();

    // Handle contractions - expand them first
    Object.entries(contractionMap).forEach(([contracted, expanded]) => {
      const regex = new RegExp(`\\b${contracted.replace("'", "\\'")}\\b`, 'gi');
      normalized = normalized.replace(regex, expanded);
    });

    return normalized;
  };

  const normalized1 = normalize(text1);
  const normalized2 = normalize(text2);

  // If completely matching after normalization and contraction expansion
  if (normalized1 === normalized2) return 100;

  // Split into words
  const words1 = normalized1.split(' ').filter(w => w.length > 0);
  const words2 = normalized2.split(' ').filter(w => w.length > 0);

  // Create frequency maps for each text
  const createWordFreq = (words: string[]): Record<string, number> => {
    const freq: Record<string, number> = {};
    words.forEach(word => {
      freq[word] = (freq[word] || 0) + 1;
    });
    return freq;
  };

  const wordFreq1 = createWordFreq(words1);
  const wordFreq2 = createWordFreq(words2);

  // Calculate matched words (intersection of word frequencies)
  let matchedWords = 0;
  const allWords = new Set([...Object.keys(wordFreq1), ...Object.keys(wordFreq2)]);

  allWords.forEach(word => {
    if (wordFreq1[word] && wordFreq2[word]) {
      matchedWords += Math.min(wordFreq1[word], wordFreq2[word]);
    }
  });

  // Use the longer text as the base for percentage calculation
  const maxWords = Math.max(words1.length, words2.length);

  if (maxWords === 0) return 0;

  // Calculate similarity percentage
  const similarity = Math.round((matchedWords / maxWords) * 100);

  return Math.min(similarity, 100); // Cap at 100%
}

// Alternative similarity calculation using Levenshtein distance for better accuracy
export function calculateLevenshteinSimilarity(text1: string, text2: string): number {
  if (!text1 || !text2) return 0;

  // Same normalization as above
  const contractionMap: Record<string, string> = {
    "we're": "we are",
    "they're": "they are",
    "you're": "you are",
    "i'm": "i am",
    "he's": "he is",
    "she's": "she is",
    "it's": "it is",
    "we've": "we have",
    "they've": "they have",
    "you've": "you have",
    "i've": "i have",
    "we'll": "we will",
    "they'll": "they will",
    "you'll": "you will",
    "i'll": "i will",
    "he'll": "he will",
    "she'll": "she will",
    "it'll": "it will",
    "won't": "will not",
    "can't": "cannot",
    "don't": "do not",
    "doesn't": "does not",
    "didn't": "did not",
    "isn't": "is not",
    "aren't": "are not",
    "wasn't": "was not",
    "weren't": "were not",
    "haven't": "have not",
    "hasn't": "has not",
    "hadn't": "had not",
    "wouldn't": "would not",
    "couldn't": "could not",
    "shouldn't": "should not",
    "let's": "let us",
    "that's": "that is",
    "there's": "there is",
    "here's": "here is",
    "where's": "where is",
    "what's": "what is",
    "who's": "who is",
    "how's": "how is",
    "when's": "when is",
    "why's": "why is",
  };

  const normalize = (text: string): string => {
    let normalized = text
      .toLowerCase()
      .replace(/[^a-z0-9\s']/g, '')
      .replace(/\s+/g, ' ')
      .trim();

    // Expand contractions
    Object.entries(contractionMap).forEach(([contracted, expanded]) => {
      const regex = new RegExp(`\\b${contracted.replace("'", "\\'")}\\b`, 'gi');
      normalized = normalized.replace(regex, expanded);
    });

    return normalized;
  };

  const s1 = normalize(text1);
  const s2 = normalize(text2);

  if (s1 === s2) return 100;

  // Calculate Levenshtein distance
  const levenshteinDistance = (str1: string, str2: string): number => {
    const matrix = Array(str2.length + 1)
      .fill(null)
      .map(() => Array(str1.length + 1).fill(null));

    for (let i = 0; i <= str1.length; i++) {
      matrix[0][i] = i;
    }

    for (let j = 0; j <= str2.length; j++) {
      matrix[j][0] = j;
    }

    for (let j = 1; j <= str2.length; j++) {
      for (let i = 1; i <= str1.length; i++) {
        const indicator = str1[i - 1] === str2[j - 1] ? 0 : 1;
        matrix[j][i] = Math.min(
          matrix[j][i - 1] + 1, // insertion
          matrix[j - 1][i] + 1, // deletion
          matrix[j - 1][i - 1] + indicator // substitution
        );
      }
    }

    return matrix[str2.length][str1.length];
  };

  const distance = levenshteinDistance(s1, s2);
  const maxLength = Math.max(s1.length, s2.length);

  if (maxLength === 0) return 100;

  const similarity = Math.round(((maxLength - distance) / maxLength) * 100);
  return Math.max(0, similarity);
}

// Combined approach for best results
export function calculateOptimalSimilarity(text1: string, text2: string): number {
  // Use word-based similarity as primary method
  const wordBasedSimilarity = calculateEnhancedSimilarity(text1, text2);

  // Use Levenshtein for cases where word-based might miss subtle differences
  const levenshteinSimilarity = calculateLevenshteinSimilarity(text1, text2);

  // Take the higher of the two scores, as either method might be more appropriate
  // depending on the type of differences
  return Math.max(wordBasedSimilarity, levenshteinSimilarity);
}